import { useEffect, useState } from 'react'
import { listUserDirectory, type DirectoryUser } from '@/api/directory'

/**
 * Who a lead can be handed to.
 *
 * Loaded when the thing that needs it opens rather than when the screen does — most visits to the
 * lead list never assign anything, and every one of these is a round trip to Keycloak behind the
 * backend. The list is short and changes about as often as the staff does, so a copy that lives as
 * long as the dialog is plenty.
 *
 * A failure is silent by design: setting a status does not need the directory, so the caller ends
 * up with an empty list and a disabled control rather than an error over a screen that otherwise
 * works.
 */
export function useColleagues(enabled: boolean): DirectoryUser[] {
  return useColleagueDirectory(enabled).colleagues
}

/**
 * The same, plus whether the answer is in — for a caller that turns ids into names and must not
 * call somebody "unknown" merely because the directory has not arrived yet. A failed load counts
 * as arrived: an empty directory is then the honest answer, not a wait that never ends.
 */
export function useColleagueDirectory(enabled: boolean): {
  colleagues: DirectoryUser[]
  loaded: boolean
} {
  const [state, setState] = useState<{ colleagues: DirectoryUser[]; loaded: boolean }>({
    colleagues: [],
    loaded: false,
  })

  useEffect(() => {
    if (!enabled) {
      return
    }
    const controller = new AbortController()
    listUserDirectory(controller.signal)
      .then((directory) => {
        if (!controller.signal.aborted) {
          setState({ colleagues: directory.users, loaded: true })
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setState((current) => ({ ...current, loaded: true }))
        }
      })
    return () => controller.abort()
  }, [enabled])

  return state
}
